import { useState, type FormEvent } from 'react';
import { api } from '../api/client';
import type { CounterRegistration, LoyaltyLookup } from '../api/types';
import { Alert, formatDate } from '../components/common';

/**
 * The counter console: find the customer, add a stamp for what they just
 * bought, or hand over a free item. Built for a phone held one-handed at a
 * till, so everything is big and there are only ever two buttons.
 */
export function StaffLoyaltyPage() {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<LoyaltyLookup[] | null>(null);
  const [selected, setSelected] = useState<LoyaltyLookup | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Signing somebody up without leaving the till. Opened from the "no one
  // found" branch, because that is the moment it is needed.
  const [signingUp, setSigningUp] = useState(false);
  const [signup, setSignup] = useState({ fullName: '', phone: '', email: '' });
  // Hidden until asked for. Most walk-ins do not give one, and a field nobody
  // fills is a field that slows down everybody who does not.
  const [wantEmail, setWantEmail] = useState(false);
  const [signupLink, setSignupLink] = useState<string | null>(null);

  /** A search term that is mostly digits is a phone number, so carry it over. */
  function openSignup() {
    const digits = term.replace(/\D/g, '');
    setSignup({
      fullName: digits.length >= 6 ? '' : term.trim(),
      phone: digits.length >= 6 ? term.trim() : '',
      email: '',
    });
    setSignupLink(null);
    setWantEmail(false);
    setSigningUp(true);
  }

  // Name plus a plausible SA number. Nothing else is required, because
  // nothing else is needed to start collecting stamps.
  const ready = signup.fullName.trim().length > 1 && signup.phone.replace(/\D/g, '').length >= 9;

  async function register(event: FormEvent) {
    event.preventDefault();

    setBusy(true);
    setError(null);

    try {
      const result: CounterRegistration = await api.staffLoyaltyRegister({
        fullName: signup.fullName.trim(),
        phone: signup.phone.trim(),
        email: signup.email.trim() || null,
      });

      // Straight onto their card, so the next tap is the stamp they came for.
      setSelected(result.card);
      setResults([result.card]);
      setMessage(result.message);
      setSignupLink(result.setPasswordLink);
      setSigningUp(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function search(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setMessage(null);
    setSelected(null);
    setBusy(true);

    try {
      const found = await api.staffLoyaltySearch(term.trim());
      setResults(found);

      // One match is the common case at a counter — skip the extra tap.
      if (found.length === 1) {
        setSelected(found[0]);
      }
    } catch (e) {
      setError((e as Error).message);
      setResults(null);
    } finally {
      setBusy(false);
    }
  }

  async function addStamp() {
    if (!selected) return;

    setBusy(true);
    setError(null);

    try {
      const result = await api.staffAddStamp(selected.customerId, 'Counter purchase');
      setSelected(result.card);
      setMessage(result.message);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function redeem() {
    if (!selected) return;

    if (!window.confirm(`Give ${selected.fullName} their free item?`)) {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const result = await api.staffRedeem(selected.customerId, 'Collected in store');
      setSelected(result.card);
      setMessage(result.message);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="page page--narrow">
      <h1>Loyalty counter</h1>
      <p className="muted">
        Find the customer by phone number, email or name, then add their stamp or hand over a free item.
      </p>

      <form className="form searchbar" onSubmit={search}>
        <input
          type="search"
          value={term}
          placeholder="0821234567, name or email"
          onChange={(e) => setTerm(e.target.value)}
          autoFocus
          enterKeyHint="search"
        />
        <button type="submit" className="btn btn--primary" disabled={busy || term.trim().length < 3}>
          Find
        </button>
      </form>

      {/* Always here, not hidden behind a failed search. Somebody asking for a
          card at the till should be two taps away, not four. */}
      {!signingUp && !selected && (
        <button type="button" className="btn btn--ghost btn--block" onClick={openSignup}>
          + New card
        </button>
      )}

      {error && <Alert>{error}</Alert>}
      {message && <Alert tone="success">{message}</Alert>}

      {results && results.length === 0 && !signingUp && (
        <Alert tone="info">No one found — “+ New card” signs them up.</Alert>
      )}

      {signingUp && (
        <form className="panel" onSubmit={register}>
          <h2 className="panel__title">New card</h2>

          <label className="field">
            <span>Name</span>
            <input
              value={signup.fullName}
              autoComplete="off"
              placeholder="Thandi Dlamini"
              autoFocus
              onChange={(e) => setSignup({ ...signup, fullName: e.target.value })}
            />
          </label>

          <label className="field">
            <span>Phone</span>
            <input
              type="tel"
              value={signup.phone}
              autoComplete="off"
              placeholder="082 111 2222"
              enterKeyHint="done"
              onChange={(e) => setSignup({ ...signup, phone: e.target.value })}
            />
          </label>

          {wantEmail ? (
            <label className="field">
              <span>Email</span>
              <input
                type="email"
                value={signup.email}
                autoComplete="off"
                placeholder="thandi@example.com"
                onChange={(e) => setSignup({ ...signup, email: e.target.value })}
              />
            </label>
          ) : (
            <button type="button" className="linkbtn" onClick={() => setWantEmail(true)}>
              + Add an email so they can use it online
            </button>
          )}

          <button type="submit" className="btn btn--primary btn--block" disabled={busy || !ready}>
            {busy ? 'Signing them up…' : 'Give them a card'}
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--block"
            onClick={() => setSigningUp(false)}
          >
            Cancel
          </button>
        </form>
      )}

      {/* Shown so it can be read out if the email does not arrive. */}
      {signupLink && (
        <Alert tone="info">
          If the email does not arrive, they can set a password here:{' '}
          <span className="muted small">{signupLink}</span>
        </Alert>
      )}

      {results && results.length > 1 && !selected && (
        <ul className="results">
          {results.map((r) => (
            <li key={r.customerId}>
              <button type="button" className="result" onClick={() => setSelected(r)}>
                <span>
                  <strong>{r.fullName}</strong>
                  <span className="muted small"> · {r.phone}</span>
                </span>
                <span className="muted small">
                  {r.stampCount}/{r.stampsPerReward}
                  {r.availableRewards > 0 && ` · ${r.availableRewards} free`}
                  {r.lifetimeRewards > 0 && ` · ${r.lifetimeRewards} card${r.lifetimeRewards === 1 ? '' : 's'} done`}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {selected && (
        <div className="panel">
          <h2>{selected.fullName}</h2>
          <p className="muted small">
            {selected.phone}
            {/* Counter sign-ups without an email carry a placeholder under the
                reserved .invalid TLD. Showing it would look like a real address
                somebody could write down. */}
            {!selected.email.endsWith('@counter.e-smoothies.invalid') && ` · ${selected.email}`}
          </p>

          <div className="stampcard stampcard--compact">
            {Array.from({ length: selected.stampsPerReward }, (_, index) => (
              <div key={index} className={index < selected.stampCount ? 'stamp is-filled' : 'stamp'}>
                {index < selected.stampCount ? '🥤' : index + 1}
              </div>
            ))}
          </div>

          <p className="center">
            <strong>
              {selected.stampCount} of {selected.stampsPerReward}
            </strong>{' '}
            · {selected.stampsUntilReward} to go
          </p>

          {/* How many cards they have finished over their whole history. The
              current card says nothing about whether this is a first visit or
              a regular of two years, and that is worth knowing while they are
              still standing there. */}
          <p className="center muted small">
            {selected.lifetimeRewards === 0
              ? `First card — ${selected.lifetimeStamps} stamp${selected.lifetimeStamps === 1 ? '' : 's'} so far`
              : `${selected.lifetimeRewards} card${selected.lifetimeRewards === 1 ? '' : 's'} completed · ${selected.lifetimeStamps} stamps all time`}
          </p>

          {selected.availableRewards > 0 && (
            <Alert tone="success">
              <strong>
                {selected.availableRewards} FREE item{selected.availableRewards > 1 ? 's' : ''} to claim
              </strong>
            </Alert>
          )}

          <button type="button" className="btn btn--primary btn--block" disabled={busy} onClick={addStamp}>
            + Add a stamp
          </button>

          <button
            type="button"
            className="btn btn--ghost btn--block"
            disabled={busy || selected.availableRewards === 0}
            onClick={redeem}
          >
            Give free item
          </button>

          <button
            type="button"
            className="btn btn--ghost btn--block"
            onClick={() => {
              setSelected(null);
              setMessage(null);
              setTerm('');
            }}
          >
            Next customer
          </button>

          {selected.activity.length > 0 && (
            <>
              <h3 className="panel__title" style={{ marginTop: '1.25rem' }}>
                Recent activity
              </h3>
              <ul className="activity">
                {selected.activity.slice(0, 6).map((entry, index) => (
                  <li key={`${entry.at}-${index}`}>
                    <span>{entry.description}</span>
                    <span className="muted small">{formatDate(entry.at)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  );
}
